import React from 'react';
import { ShieldOff } from 'lucide-react';

/**
 * Role required for application access (display only — enforcement is server-side).
 * Kept in sync with backend's REQUIRED_SSO_ROLE in auth_config.py.
 */
const REQUIRED_ROLE_DISPLAY = 'Payroll Agent';

/**
 * Full-screen, non-dismissible "Access Denied" page.
 *
 * Rendered when the backend SSO callback detects the user's JWT is missing
 * the required "Payroll Agent" role. Because no cookie is ever set for
 * unauthorized users, this page has zero authenticated session — even if a
 * user navigates away, every API call will 401.
 */
const UnauthorizedPage: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const reason = params.get('reason');

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[100] p-4 bg-black/80 backdrop-blur-md">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full h-[90vh] overflow-hidden flex flex-col ring-4 ring-red-400/60">
        {/* ── Header ──────────────────────────────────────── */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 px-8 py-12">
          <div className="flex items-center space-x-5">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
              <ShieldOff className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-white">Access Denied</h2>
              <p className="text-base font-medium text-red-200 mt-1">
                You are not authorized to access this application
              </p>
            </div>
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────── */}
        <div className="px-8 py-10 bg-neutral-50/50 flex-1">
          <div className="p-8 bg-red-50 rounded-xl border border-red-200">
            <div className="flex items-start space-x-4">
              <ShieldOff className="w-8 h-8 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-lg font-bold text-red-800">Unauthorized Access</p>
                <p className="text-base text-red-700 mt-2">
                  Your account does not have the required &quot;
                  {REQUIRED_ROLE_DISPLAY}&quot; role to use this application.
                </p>
                {reason === 'role_revoked' && (
                  <p className="text-base text-red-700 mt-3">
                    Your access was revoked during your session. If you believe this is
                    an error, contact your administrator.
                  </p>
                )}
                <p className="text-sm text-red-600 mt-4 font-medium">
                  Contact your administrator to request the &quot;
                  {REQUIRED_ROLE_DISPLAY}&quot; role.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────── */}
        <div className="px-8 py-6 border-t border-neutral-200 bg-neutral-100">
          <p className="text-sm text-neutral-500 text-center">
            This page cannot be dismissed. Close the browser tab to exit.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage;
